import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import CompanyHierarchy from "@/components/company/companyHierarchy";
import { LinearProgress } from "@mui/material";
import styles from "@/components/company/companyHierarchyModal.module.css";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

interface CompanyTreeNode {
  id: string;
  name: string;
  children?: CompanyTreeNode[];
}

interface CompanyHierarchyModalProps {
  open: boolean;
  onClose: () => void;
  subjectId: string;
  selectedCompanyId?: string;
  selectedSubjectId?: string;
  disableClickForHighlightedCompany?: boolean;
}

const CompanyHierarchyModal: React.FC<CompanyHierarchyModalProps> = ({
  open,
  onClose,
  subjectId,
  selectedCompanyId,
  selectedSubjectId,
  disableClickForHighlightedCompany = false,
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [treeData, setTreeData] = useState<CompanyTreeNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (open && subjectId) {
      fetchCompanyTree();
    } else {
      setTreeData(null);
      setError(null);
    }
  }, [open, subjectId]);

  const fetchCompanyTree = async () => {
    setLoading(true);
    setError(null);

    try {
      const { api } = await import("@/helper/api");
      const additionalUrlParameters: any = {
        show_tree: true,
      };

      api.genericApiRequest({
        entity: "subject",
        method: "get",
        entityId: subjectId,
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameters,
        },
        successHandler: (res: any) => {
          const completeTree = res.data?.complete_company_tree;
          if (completeTree) {
            setTreeData(completeTree);
          } else {
            setError(t("no_hierarchy_data_available"));
          }
          setLoading(false);
        },
        failHandler: (error: any) => {
          setError(error);
          setLoading(false);
        },
      });
    } catch (err: any) {
      setError(err);
      setLoading(false);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  const cancelClose = useModalDismiss(open, handleClose);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      fullScreen={isFullscreen}
      {...cancelClose}
      PaperProps={{
        sx: {
          width: isFullscreen ? "100%" : "90vw",
          height: isFullscreen ? "100%" : "85vh",
          maxWidth: isFullscreen ? "100%" : "90vw",
          maxHeight: isFullscreen ? "100%" : "85vh",
          borderRadius: isFullscreen ? 0 : "0.25rem",
        },
      }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Box className={styles.dialogTitleContent}>
          <Box className={styles.dialogTitleContentItem}>
            <span className={styles.dialogTitleContentItemTitle}>
              {t("company_hierarchy")}
            </span>
            <span className={styles.dialogTitleContentItemSubText}>
              {t("click_node_to_navigate_to_company_subject_overview")}
            </span>
          </Box>
        </Box>
        <Box className={styles.dialogTitleContentButtons}>
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={isFullscreen ? t("exit_fullscreen") : t("enter_fullscreen")}
          >
            <IconButton onClick={handleToggleFullscreen} size="small">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("close")}
          >
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {loading && (
          <Box className={styles.dialogContentLoading}>
            <LinearProgress />
          </Box>
        )}
        {error && (
          <Box className={styles.dialogContentError}>
            {typeof error === "string" ? error : t("error_loading_company_hierarchy")}
          </Box>
        )}
        {!loading && !error && treeData && (
          <Box className={styles.dialogContentCompanyHierarchy}>
            <CompanyHierarchy
              data={treeData}
              selectedCompanyId={selectedCompanyId}
              selectedSubjectId={selectedSubjectId || subjectId}
              disableClickForHighlightedCompany={disableClickForHighlightedCompany}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyHierarchyModal;
